import { jsx } from "panel";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ItemsMenuEl } from "common/components/items-menu";
import { ButtonBarEl } from "common/components/button-bar";
import { ToggleEl } from "common/components/toggle";
import { ToggleTheme } from "common/components/toggle/types";
import { SkeletonListRowSize } from "common/components/skeleton-list/types";
import { SkeletonListEl } from "common/components/skeleton-list";
import { Placement } from "common/components/tooltip/types";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { ResourceType } from "common/report/constants";
import { ButtonEl } from "common/components/button";
import { renameEvent } from "common/report/util/events";
import { MenuSearchBar } from "../mixins/search";
import { BetaMenuActionBar } from "../mixins/beta-action-bar";
import { FilterDropdownWrapperEl } from "../filter-dropdown-wrapper";
import { AllPropertiesTab, SortBy, StagedItemType } from "./types";
export function template() {
  if (!this.attr(`open`)) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-properties-menu",
      class: { "beta-menu": this.isBetaMenu(), "mp-child-menu": true },
      hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
    });
  }
  const isBetaMenu = this.isBetaMenu();
  const menuKeyTag = isBetaMenu ? `beta` : `classic`;
  const keydownProxyEl = isBetaMenu || this.hasStagedItem() ? document.body : this.state.keydownProxyEl;
  const createSectionItems = this.helpers.getCreateSectionItems();
  const CreateItemsMenuEl = () => {
    const hasAddFilterButton = this.helpers.useMultipleSelection();
    return createSectionItems.length > 0 ? createSectionItems.length === 1 ? /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".create-custom-button",
      attrs: {
        "show-preview-tooltip": false,
        theme: ButtonTheme.MenuFooter,
        "hide-input-focus-styling": true,
        label: `Create Custom ${createSectionItems[0].label}`
      },
      on: {
        click: () => this.showCreateItemModal(createSectionItems[0].value)
      }
    }, /* @__PURE__ */ jsx("div", {
      sel: ".create-custom-label",
      attrs: { elref: `label` }
    }, "Create Custom ", createSectionItems[0].label)) : /* @__PURE__ */ jsx(SelectEl, {
      sel: ".create-custom-button",
      attrs: {
        "disable-search": true,
        "show-preview-tooltip": false,
        sections: [{ items: createSectionItems }],
        theme: SelectTheme.MenuFooter,
        size: ButtonSize.Inline,
        label: `Create Custom...`,
        "hide-input-focus-styling": true,
        "menu-width": 240,
        "no-rounded-corners": hasAddFilterButton
      },
      on: { change: this.helpers.handleCreateItemClick }
    }) : null;
  };
  const tabs = this.getToggleTabs();
  const hasTabbedMenu = tabs.length > 1;
  const TabbedHeaderEl = () => {
    return hasTabbedMenu && /* @__PURE__ */ jsx(ToggleEl, {
      attrs: {
        selected: this.state.selectedTab,
        theme: isBetaMenu ? ToggleTheme.TabBarLite : ToggleTheme.TabBar
      },
      key: `tabs-${tabs.length}`,
      on: { select: this.helpers.handleTabSelect }
    }, tabs.map((tab) => /* @__PURE__ */ jsx("mp-toggle-option", {
      attrs: {
        value: tab.value,
        disabled: tab.disabled
      }
    }, tab.label)));
  };
  const InnerSearch = MenuSearchBar({
    value: this.state.searchFilter,
    handleSearchInput: this.helpers.handleSearchInput,
    backButtonClick: this.hasStagedItem() && this.helpers.unstageProperty || this.backButtonHandler,
    onInsert: (el) => this.update({ keydownProxyEl: el }),
    key: `${menuKeyTag}-search`,
    newStyling: isBetaMenu
  });
  const showLoadingSkeleton = isBetaMenu && this.state.isLoading;
  const FilterSelectEl = () => /* @__PURE__ */ jsx(FilterDropdownWrapperEl, {
    attrs: {
      "always-show-caret": true,
      theme: SelectTheme.MenuFilter,
      size: ButtonSize.Inline,
      sections: [{ label: `Filter by`, items: this.helpers.getFilterOptionSectionItems() }],
      "menu-width": null
    },
    props: {
      handleFilterChange: this.helpers.handleFilterChange
    }
  });
  const SortSelectEl = () => /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      "icon-only": true,
      icon: {
        leftIcon: (() => {
          switch (this.state.sortByValue) {
            case SortBy.NameAsc:
              return `sort-alpha-asc`;
            case SortBy.NameDesc:
              return `sort-alpha-desc`;
            default:
              return `relevancy`;
          }
        })()
      },
      theme: SelectTheme.Tertiary,
      size: ButtonSize.Medium,
      sections: [{ label: `Sort By`, items: this.helpers.getSortingOptionSectionItems() }],
      "menu-width": null,
      tooltip: { placement: Placement.Left }
    },
    on: {
      change: this.helpers.handleSortChange
    }
  });
  const SortFilterRowEl = () => {
    let showing;
    let action = this.state.searchFilter ? `Searching ` : `Show`;
    let target = ``;
    switch (this.state.selectedTab) {
      case AllPropertiesTab:
      case ResourceType.Cohort:
        showing = /* @__PURE__ */ jsx(FilterSelectEl, null);
        break;
      case `events`:
        showing = /* @__PURE__ */ jsx("div", null, `events`);
        break;
      default:
        return null;
    }
    if (this.state.stagedItemType && !this.isUnsortableStage()) {
      switch (this.state.stagedItemType) {
        case StagedItemType.BehaviorMultiAttribution:
          action = `Attributed by...`;
          break;
        case StagedItemType.BehaviorWithEvent:
          action = `Select Event...`;
          break;
        case StagedItemType.EventWithProperties:
          action = `Select Property on `;
          target = `${renameEvent(this.state.stagedItemsStack[this.state.stagedItemsStack.length - 1].value.value)}...`;
          break;
      }
      showing = ``;
    }
    if (this.attr(`property-of-label`)) {
      action = `Select Property on `;
      target = `${this.attr(`property-of-label`)}...`;
      showing = null;
    }
    return !this.isUnsortableStage() ? /* @__PURE__ */ jsx("div", {
      sel: ".header-filter-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".showing-text-wrapper"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".showing-text"
    }, action, target && /* @__PURE__ */ jsx("b", null, target)), showing), /* @__PURE__ */ jsx(SortSelectEl, null)) : /* @__PURE__ */ jsx("div", {
      sel: ".empty-header-filter-row"
    });
  };
  const MenuContent = /* @__PURE__ */ jsx(null, null, InnerSearch, /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-header"
  }, /* @__PURE__ */ jsx(TabbedHeaderEl, null), this.isBetaMenu() && /* @__PURE__ */ jsx(SortFilterRowEl, null), /* @__PURE__ */ jsx("slot", {
    attrs: { name: `menu-header` }
  })), showLoadingSkeleton ? /* @__PURE__ */ jsx(SkeletonListEl, {
    attrs: { "num-rows": 8, "row-size": SkeletonListRowSize.Large },
    sel: ".mp-properties-menu-skeleton-list"
  }) : /* @__PURE__ */ jsx(ItemsMenuEl, {
    sel: ".mp-properties-menu-items-menu",
    key: [
      menuKeyTag,
      this.hasStagedItem() ? `stageditem:${this.peekStagedItem().label}` : `tab:${this.state.selectedTab}`
    ].join(`-`),
    attrs: {
      "empty-message": `No items found`,
      "search-filter": this.getSearchFilter(),
      multiple: this.helpers.useMultipleSelection(),
      "select-all-mechanism": this.attr(`select-all-mechanism`),
      "show-select-all": this.attr(`show-select-all`),
      "select-all-disabled": this.isSelectAllDisabled(),
      "start-focus-on-selected": true,
      ...isBetaMenu ? {
        "disable-search-sort": true,
        "disable-search-filter-matching-parts": true,
        "disable-first-item-padding": true,
        "new-styling": true
      } : {}
    },
    on: {
      click: (ev) => ev.stopPropagation(),
      clickExpanderItem: this.handleExpandItemClick.bind(this),
      clickItem: this.helpers.handleClickItem,
      clickPill: this.helpers.onItemPillClick,
      selectAll: this.helpers.handleSelectAll,
      tooltipContextAction: this.helpers.handleTooltipContextAction,
      tooltipContextEditDescription: this.helpers.handleTooltipContextEditDescription
    },
    props: {
      sections: this.helpers.getMenuSections(),
      keydownProxyEl: !this.state.isModalOpen && keydownProxyEl,
      sectionsFromInputFn: (input, _sections) => this.helpers.sectionsFromInput(input),
      customSearchMatcherFn: this.helpers.customSearchMatcherFn,
      contextPanelTransform: this.helpers.contextPanelTransform,
      contextPanelPositionParentElement: this.helpers.getChildMenuRef()
    }
  }));
  const ApplyButton = this.shouldShowApplyButton() && /* @__PURE__ */ jsx(ButtonBarEl, {
    sel: ".mp-button-bar-primary",
    attrs: {
      role: `button`,
      elref: `applyButton`,
      disabled: this.isApplyButtonDisabled()
    },
    on: {
      click: this.helpers.onApplyButtonClick
    }
  }, this.getApplyButtonText());
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu",
    class: { "beta-menu": this.isBetaMenu(), "mp-child-menu": true },
    hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
  }, MenuContent, this.state.canUseBetaMenu ? /* @__PURE__ */ jsx(BetaMenuActionBar, {
    isBetaMenu,
    canCreateCustom: Boolean(createSectionItems.length > 0),
    setBetaVersion: this.setBetaVersion.bind(this)
  }) : null, this.isBetaMenu() ? /* @__PURE__ */ jsx(CreateItemsMenuEl, null) : null, ApplyButton);
}
